/** @file installShortcuts: the one `listen(window)` of the editor plus the shell's global bindings. */
import { applyOverrides } from '@zilo/pose3d-shortcuts/applyOverrides';
import type { KeyTarget } from '@zilo/pose3d-shortcuts/keyTarget';
import { listen } from '@zilo/pose3d-shortcuts/listen';
import { registerBindings } from '@zilo/pose3d-shortcuts/registerBindings';
import type { Binding, Platform } from '@zilo/pose3d-shortcuts/types';
import type { EditorStore } from '../state/editorTypes';
import { escapeLadder } from './escapeLadder';
import { menuHandlers } from './menuHandlers';
import { shellBindings } from './shellBindings';
import { shellHandlers } from './shellHandlers';
import { shellMenuBindings } from './shellMenuBindings';
import { transportBindings } from './transportBindings';
import { transportHandlers } from './transportHandlers';

/** The key target (default `window`), the platform for `Mod`, and the user's overrides (Settings). */
export type InstallOptions = { target?: KeyTarget; platform: Platform; overrides: Binding[] };

/** The `when` names the editor's tables use: `poseScreen` = a puppet's Pose screen shows, `tileScreen` = a Tile tab. */
const conditions = (store: EditorStore) => (name: string): boolean => {
  const screen = store.getState().route.screen;
  return (name === 'poseScreen' && screen === 'pose') || (name === 'tileScreen' && screen === 'tile');
};

/**
 * Listens once and registers the shell's tables (`shellBindings`, `shellMenuBindings`, `transportBindings`; overrides
 * applied) in `global`; Escape runs the shell's `escapeLadder`. The disposer undoes both.
 */
export function installShortcuts(store: EditorStore, options: InstallOptions): () => void {
  const stop = listen(options.target ?? window, { platform: options.platform, when: conditions(store) });
  const all = [...shellBindings, ...shellMenuBindings, ...transportBindings];
  const { table } = applyOverrides(all, options.overrides, options.platform);
  const handlers = { ...shellHandlers(store), ...menuHandlers(store), ...transportHandlers(store), 'app.escape': () => { escapeLadder(store); } };
  const unregister = registerBindings('global', table, handlers);
  return () => {
    unregister();
    stop();
  };
}
